// src/components/admin/questions/PasteAlternativesModal.tsx
// ✅ Layout largo (780px), body scrollável, identidade Graia + dark mode

import React, { useState, useRef, useEffect } from 'react';
import { X, ClipboardPaste, CheckCircle, AlertTriangle } from 'lucide-react';

interface PasteAlternativesModalProps {
    onConfirm: (lines: string[]) => void;
    onClose: () => void;
}

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

function cleanLine(line: string): string {
    return line
        .trim()
        .replace(/^[A-Ea-e1-5][).\-:]\s*/, '')
        .trim();
}

const PasteAlternativesModal: React.FC<PasteAlternativesModalProps> = ({ onConfirm, onClose }) => {
    const [raw, setRaw] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => { textareaRef.current?.focus(); }, []);

    const lines = raw.split('\n').map(cleanLine).filter(l => l.length > 0).slice(0, 5);
    const canConfirm = lines.length >= 2;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    };

    return (
        <div
            className="pam-overlay"
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={handleKeyDown}
        >
            <div className="pam-modal">

                {/* ── Header (fixo) ── */}
                <div className="pam-header">
                    <div className="pam-header-left">
                        <ClipboardPaste size={18} />
                        <span>Colar Alternativas</span>
                    </div>
                    <button className="pam-close" onClick={onClose} title="Fechar (Esc)">
                        <X size={16} />
                    </button>
                </div>

                {/* ── Body (scrollável) ── */}
                <div className="pam-body">
                    <p className="pam-hint">
                        Cole uma alternativa por linha. Prefixos como <code>A)</code>, <code>B.</code>, <code>1-</code> são removidos automaticamente.
                    </p>

                    <textarea
                        ref={textareaRef}
                        className="pam-textarea"
                        value={raw}
                        onChange={e => setRaw(e.target.value)}
                        placeholder={`Texto alternativa 1\nTexto alternativa 2\nTexto alternativa 3\nTexto alternativa 4\nTexto alternativa 5\n\nOu com prefixos:\nA) Texto alternativa 1\nB) Texto alternativa 2`}
                        rows={8}
                    />

                    {lines.length > 0 && (
                        <div className="pam-preview">
                            <span className="pam-preview-label">Preview</span>
                            <div className="pam-preview-list">
                                {lines.map((line, i) => (
                                    <div key={i} className="pam-preview-item">
                                        <span className="pam-preview-letter">{LETTERS[i]}</span>
                                        <span className="pam-preview-text">{line}</span>
                                    </div>
                                ))}
                            </div>
                            {lines.length < 2 && (
                                <div className="pam-warn">
                                    <AlertTriangle size={13} />
                                    Mínimo de 2 alternativas
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer (fixo) ── */}
                <div className="pam-footer">
                    <span className="pam-count">
                        {lines.length > 0
                            ? `${lines.length} alternativa${lines.length > 1 ? 's' : ''} detectada${lines.length > 1 ? 's' : ''}`
                            : 'Aguardando...'}
                    </span>
                    <div className="pam-footer-actions">
                        <button className="pam-btn pam-btn--cancel" onClick={onClose}>Cancelar</button>
                        <button
                            className="pam-btn pam-btn--confirm"
                            onClick={() => canConfirm && onConfirm(lines)}
                            disabled={!canConfirm}
                        >
                            <CheckCircle size={15} />
                            Preencher automaticamente
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .pam-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    backdrop-filter: blur(4px);
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: pamFadeIn 0.15s ease;
                }
                @keyframes pamFadeIn { from{opacity:0} to{opacity:1} }

                /* Modal: largo, altura máxima, flex-column para fixar header/footer */
                .pam-modal {
                    background: var(--bg-card);
                    border: 1px solid rgba(0, 59, 209,0.25);
                    border-radius: 14px;
                    width: 100%;
                    max-width: 780px;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 24px 64px rgba(0,0,0,0.22);
                    animation: pamSlideUp 0.18s ease;
                    overflow: hidden;
                }
                @keyframes pamSlideUp {
                    from{transform:translateY(14px);opacity:0}
                    to{transform:translateY(0);opacity:1}
                }

                /* Header — não encolhe */
                .pam-header {
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 24px;
                    border-bottom: 1px solid rgba(0, 59, 209,0.2);
                }
                .pam-header-left {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    font-weight: 700;
                    font-size: 15px;
                    color: var(--text-primary);
                }
                .pam-header-left svg { color: var(--btn-primary, #003BD1); }
                .pam-close {
                    background: none; border: none; cursor: pointer;
                    color: var(--text-secondary); padding: 6px; border-radius: 7px;
                    display: flex; align-items: center; justify-content: center;
                    transition: background .15s, color .15s;
                }
                .pam-close:hover { background: rgba(239,68,68,0.1); color: #dc2626; }

                /* Body — cresce, rola */
                .pam-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                    scrollbar-width: thin;
                    scrollbar-color: var(--btn-primary,#003BD1) rgba(0, 59, 209,0.1);
                }
                .pam-body::-webkit-scrollbar { width: 5px; }
                .pam-body::-webkit-scrollbar-track { background: rgba(0, 59, 209,0.08); border-radius: 4px; }
                .pam-body::-webkit-scrollbar-thumb { background: var(--btn-primary,#003BD1); border-radius: 4px; }

                .pam-hint {
                    font-size: 13px; color: var(--text-secondary);
                    line-height: 1.55; margin: 0;
                }
                .pam-hint code {
                    background: rgba(0, 59, 209,0.1);
                    color: var(--btn-primary,#003BD1);
                    padding: 1px 6px; border-radius: 4px;
                    font-family: monospace; font-size: 12px; font-weight: 600;
                }

                .pam-textarea {
                    width: 100%;
                    min-height: 160px;
                    padding: 13px 15px;
                    font-size: 13.5px; line-height: 1.65;
                    font-family: inherit;
                    border: 1.5px solid rgba(0, 59, 209,0.25);
                    border-radius: 9px;
                    background: var(--bg-secondary, var(--bg-main, #f8fafc));
                    color: var(--text-primary);
                    resize: vertical;
                    transition: border-color .15s, box-shadow .15s;
                    box-sizing: border-box;
                }
                .pam-textarea:focus {
                    outline: none;
                    border-color: var(--btn-primary,#003BD1);
                    box-shadow: 0 0 0 3px rgba(0, 59, 209,0.12);
                    background: var(--bg-card);
                }
                .pam-textarea::placeholder {
                    color: var(--text-secondary); opacity: .5; white-space: pre-line;
                }

                .pam-preview {
                    background: rgba(0, 59, 209,0.05);
                    border: 1px solid rgba(0, 59, 209,0.2);
                    border-radius: 9px; padding: 14px 16px;
                    display: flex; flex-direction: column; gap: 10px;
                }
                .pam-preview-label {
                    font-size: 11px; font-weight: 700; text-transform: uppercase;
                    letter-spacing: .07em; color: var(--btn-primary,#003BD1);
                }
                .pam-preview-list { display: flex; flex-direction: column; gap: 7px; }
                .pam-preview-item {
                    display: flex; align-items: flex-start; gap: 11px; font-size: 13.5px;
                }
                .pam-preview-letter {
                    font-weight: 700; color: var(--btn-primary,#003BD1);
                    min-width: 18px; padding-top: 1px; flex-shrink: 0;
                }
                .pam-preview-text { color: var(--text-primary); line-height: 1.45; word-break: break-word; }
                .pam-warn {
                    display: flex; align-items: center; gap: 6px;
                    font-size: 12px; color: #d97706; font-weight: 600;
                }

                /* Footer — não encolhe */
                .pam-footer {
                    flex-shrink: 0;
                    padding: 14px 24px;
                    border-top: 1px solid rgba(0, 59, 209,0.2);
                    background: rgba(0, 59, 209,0.04);
                    display: flex; align-items: center;
                    justify-content: space-between; gap: 12px;
                }
                .pam-count { font-size: 12.5px; color: var(--text-secondary); font-weight: 500; }
                .pam-footer-actions { display: flex; gap: 9px; }

                .pam-btn {
                    display: flex; align-items: center; gap: 7px;
                    padding: 9px 18px; border-radius: 8px;
                    font-size: 13.5px; font-weight: 600;
                    cursor: pointer; border: none;
                    transition: all .15s; letter-spacing: .2px;
                }
                .pam-btn--cancel {
                    background: var(--bg-card);
                    border: 1.5px solid rgba(0, 59, 209,0.25);
                    color: var(--text-secondary);
                }
                .pam-btn--cancel:hover {
                    background: rgba(0, 59, 209,0.07);
                    border-color: var(--btn-primary,#003BD1);
                    color: var(--btn-primary,#003BD1);
                }
                .pam-btn--confirm {
                    background: var(--btn-primary,#003BD1); color:#fff;
                    box-shadow: 0 2px 8px rgba(0, 59, 209,0.25);
                }
                .pam-btn--confirm:hover:not(:disabled) {
                    filter: brightness(1.08);
                    transform: translateY(-1px);
                    box-shadow: 0 5px 14px rgba(0, 59, 209,0.32);
                }
                .pam-btn--confirm:disabled { opacity:.4; cursor:not-allowed; box-shadow:none; }

                @media (max-width:600px) {
                    .pam-modal { max-width:100%; border-radius:12px; }
                    .pam-footer { flex-direction:column; align-items:stretch; gap:10px; }
                    .pam-footer-actions { justify-content:flex-end; }
                    .pam-count { text-align:center; }
                }
            `}</style>
        </div>
    );
};

export default PasteAlternativesModal;
